/**
 * Vehicle facet `param_name` strings must stay identical to the CSV emitted by
 * `snippets/vehicle-facet-param-list.liquid` (single source of truth in Liquid).
 * Change both when renaming or adding vehicle metafacet filters.
 */
const VEHICLE_FACET_PARAMS = {
  BRAND: 'filter.p.m.sga.nest_vehicle_brand',
  MODEL: 'filter.p.m.sga.nest_vehicle_model',
  BRAND_MODEL: 'filter.p.m.sga.nest_vehicle_brand_model',
};

function escapeCssAttr(value) {
  const s = String(value);
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(s);
  }
  return s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

if (!customElements.get('vehicle-filter')) {
  customElements.define(
    'vehicle-filter',
    class VehicleFilter extends HTMLElement {
      constructor() {
        super();

        this.facetUpdateUnsubscriber = undefined;
        this._onDelegatedChange = this._onDelegatedChange.bind(this);
        this.onFacetUpdate = this.onFacetUpdate.bind(this);
      }

      connectedCallback() {
        this.addEventListener('change', this._onDelegatedChange, true);

        const themeApi = window.theme;
        if (themeApi && themeApi.pubsub && themeApi.pubsub.subscribe) {
          this.facetUpdateUnsubscriber = themeApi.pubsub.subscribe(
            themeApi.pubsub.PUB_SUB_EVENTS.facetUpdate,
            this.onFacetUpdate
          );
        }
      }

      disconnectedCallback() {
        this.removeEventListener('change', this._onDelegatedChange, true);

        if (this.facetUpdateUnsubscriber) {
          this.facetUpdateUnsubscriber();
        }
      }

      get facetForm() {
        const sectionId = this.dataset.sectionId;
        if (sectionId) {
          const scoped = document.querySelector(
            `form[is="facet-form"][data-section-id="${escapeCssAttr(sectionId)}"]`
          );
          if (scoped) {
            return scoped;
          }
        }
        const sectionEl = this.closest('section');
        if (sectionEl) {
          const inSection = sectionEl.querySelector('form[is="facet-form"]');
          if (inSection) {
            return inSection;
          }
        }
        return document.querySelector('form[is="facet-form"]');
      }

      /**
       * Single delegated handler — catches change events from any
       * brand/model radio, including those injected by vehicle-selector.js
       * after the initial render.
       */
      _onDelegatedChange(event) {
        const target = event.target;
        if (target.matches('[data-vehicle-brand-input]')) {
          this.onBrandInputChange(event);
        } else if (target.matches('[data-vehicle-model-input]')) {
          this.onModelInputChange(event);
        }
      }

      buildCurrentUrl() {
        return new URL(window.location.href);
      }

      updateBrowserUrl(url) {
        const cleanUrl = new URL(url.toString());
        cleanUrl.searchParams.delete('section_id');
        history.replaceState({}, '', cleanUrl.toString());
      }

      renderWithFacetForm(url, event) {
        const form = this.facetForm;

        if (!form) {
          window.location.href = url.toString();
          return;
        }

        const requestUrl = new URL(url.toString());
        const themeApi = window.theme;
        const utils = themeApi && themeApi.utils;
        const sectionIdForRequest =
          utils && typeof utils.sectionId === 'function'
            ? utils.sectionId(form)
            : form.dataset.sectionId || form.getAttribute('data-section-id');
        if (!sectionIdForRequest) {
          window.location.href = url.toString();
          return;
        }
        requestUrl.searchParams.set('section_id', sectionIdForRequest);
        form.renderSection(requestUrl.toString(), event);
        this.updateBrowserUrl(requestUrl);
      }

      onBrandInputChange(event) {
        event.stopPropagation();

        if (this.dataset.clientSideFilter === 'active') {
          delete this.dataset.clientSideFilter;
          delete this.dataset.activeModelGid;
          this._publishModelFilter(null);
        }

        const url = this.buildCurrentUrl();
        const brandValue = event.target.value;

        if (brandValue) {
          url.searchParams.set(VEHICLE_FACET_PARAMS.BRAND, brandValue);
        } else {
          url.searchParams.delete(VEHICLE_FACET_PARAMS.BRAND);
        }

        url.searchParams.delete(VEHICLE_FACET_PARAMS.BRAND_MODEL);
        url.searchParams.delete(VEHICLE_FACET_PARAMS.MODEL);
        url.searchParams.delete('vehicle_model');
        url.searchParams.delete('page');

        this.renderWithFacetForm(url, event);
      }

      updateModelSortUi(labelText) {
        const modelSortEl = this.querySelector('.vehicle-filter__sort--model');
        if (!modelSortEl) return;
        const selection = modelSortEl.querySelector('.sort-selection');
        if (selection) selection.textContent = labelText;
        const sortBy = modelSortEl.querySelector('.sort-by');
        if (sortBy) sortBy.removeAttribute('open');
        if (typeof modelSortEl.initButton === 'function') modelSortEl.initButton();
      }

      _publishModelFilter(modelGid) {
        const themeApi = window.theme;
        if (themeApi && themeApi.pubsub) {
          themeApi.pubsub.publish('vehicle:modelFilter', {
            modelGid: modelGid,
            sectionId: this.dataset.sectionId,
          });
        }
      }

      /**
       * All model filtering is client-side. When a model is selected the URL
       * stays at brand-only; vehicle-selector.js loads any remaining paginated
       * pages and hides non-matching product cards using the data-vehicle-models
       * DOM attribute. When "All models" is selected, all cards are restored.
       */
      onModelInputChange(event) {
        event.stopPropagation();
        const modelValue = event.target.value;

        if (modelValue) {
          this.dataset.clientSideFilter = 'active';
          this.dataset.activeModelGid = modelValue;

          const label = event.target.nextElementSibling;
          if (label) this.updateModelSortUi(label.textContent.trim());

          const currentUrl = new URL(window.location.href);
          const hasServerModelFilter = currentUrl.searchParams.has(VEHICLE_FACET_PARAMS.BRAND_MODEL);

          if (hasServerModelFilter) {
            const brandUrl = this.buildCurrentUrl();
            brandUrl.searchParams.delete(VEHICLE_FACET_PARAMS.BRAND_MODEL);
            const brandInput = this.querySelector('[data-vehicle-brand-input]:checked');
            if (brandInput && brandInput.value) {
              brandUrl.searchParams.set(VEHICLE_FACET_PARAMS.BRAND, brandInput.value);
            }
            brandUrl.searchParams.delete(VEHICLE_FACET_PARAMS.MODEL);
            brandUrl.searchParams.delete('page');
            brandUrl.searchParams.set('vehicle_model', modelValue);
            this.dataset.pendingModelGid = modelValue;
            this.renderWithFacetForm(brandUrl, event);
          } else {
            const shareUrl = this.buildCurrentUrl();
            shareUrl.searchParams.set('vehicle_model', modelValue);
            shareUrl.searchParams.delete(VEHICLE_FACET_PARAMS.BRAND_MODEL);
            this.updateBrowserUrl(shareUrl);
            this._publishModelFilter(modelValue);
          }
          return;
        }

        if (this.dataset.clientSideFilter === 'active') {
          delete this.dataset.clientSideFilter;
          delete this.dataset.activeModelGid;

          const clearUrl = this.buildCurrentUrl();
          clearUrl.searchParams.delete('vehicle_model');
          this.updateBrowserUrl(clearUrl);

          const allLabel = this.querySelector(
            '.vehicle-filter__sort--model .sort-listbox li:first-child label'
          );
          if (allLabel) this.updateModelSortUi(allLabel.textContent.trim());

          this._publishModelFilter(null);
          return;
        }

        const url = this.buildCurrentUrl();
        url.searchParams.delete(VEHICLE_FACET_PARAMS.BRAND_MODEL);
        url.searchParams.delete(VEHICLE_FACET_PARAMS.MODEL);
        url.searchParams.delete('vehicle_model');
        const brandInput = this.querySelector('[data-vehicle-brand-input]:checked');
        if (brandInput && brandInput.value) {
          url.searchParams.set(VEHICLE_FACET_PARAMS.BRAND, brandInput.value);
        } else {
          url.searchParams.delete(VEHICLE_FACET_PARAMS.BRAND);
        }
        url.searchParams.delete('page');
        this.renderWithFacetForm(url, event);
      }

      onFacetUpdate(event) {
        const html = event && event.responseText;
        if (typeof html !== 'string' || html === '') {
          return;
        }
        const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
        const sectionId = this.dataset.sectionId;
        const scopedSelector = sectionId
          ? `vehicle-filter[data-section-id="${escapeCssAttr(sectionId)}"]`
          : 'vehicle-filter';
        const updatedVehicleFilter =
          parsedHTML.querySelector(scopedSelector) || parsedHTML.querySelector('vehicle-filter');

        if (!updatedVehicleFilter) {
          this.innerHTML = '';
          return;
        }

        this.innerHTML = updatedVehicleFilter.innerHTML;
      }
    }
  );
}
